import { useCallback } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { RootState, useAppSelector } from 'src/store';
import { useContactsApi } from '../api';
import {
  resetError,
  resetListWithGroups as resetList,
  setError,
  setFilterWithGroups as setFilter,
  setListWithGroups as setList,
  setLoading,
} from '../slices';

const getServerSortField = (sort?: string) => {
  if (!sort) return undefined;

  const sortMap: Record<string, string> = {
    phoneNumber: 'phone_number',
    validStatus: 'valid_status',
    optInDate: 'opt_in_date',
    optInType: 'opt_in_type',
    optOutDate: 'opt_out_date',
    optOutType: 'opt_out_type',
    createdBy: 'created_by',
    createdAt: 'date_created',
    updatedAt: 'date_updated',
    dateAdded: 'date_added',
  };

  const [field, order] = sort.split('_');
  const serverField = sortMap[field] || field;
  return `${serverField}_${order}`;
};

export const useContactsWithGroupsService = () => {
  const dispatch = useDispatch();
  const { getState } = useStore<RootState>();
  const {
    loading,
    filterWithGroups: filter,
    listWithGroups: list,
    error,
  } = useAppSelector(state => state.contacts);

  const api = useContactsApi();

  const getContacts = useCallback(async (companyId: string) => {
    const filter = getState().contacts.filterWithGroups;

    try {
      dispatch(resetError());
      dispatch(setLoading({ getListWithGroups: true }));

      const response = await api.getContactsWithGroups({
        companyId,
        limit: filter.limit,
        offset: filter.offset,

        query: filter.query || undefined,
        sort: getServerSortField(filter.sort),
        groups: filter.groups || undefined,
        notInGroups: filter.notInGroups || undefined,
        validStatus: filter.validStatus || undefined,
        isSpam: filter.isSpam || undefined,
      });
      dispatch(setList(response.data));

      return response.data;
    } catch (e) {
      dispatch(setError(e));
      throw e;
    } finally {
      dispatch(setLoading({ getListWithGroups: false }));
    }
  }, []);

  const changeContactsFilter = useCallback(
    (filter: Partial<RootState['contacts']['filterWithGroups']>) => {
      dispatch(setFilter(filter));
    },
    [],
  );

  const resetContacts = useCallback(() => {
    dispatch(resetList());
  }, []);

  return {
    loading,
    filter,
    list,
    error,
    getContacts,
    changeContactsFilter,
    resetContacts,
  };
};
